import { useTypedSetCache } from 'apiTyped/helpers/useTypedSetCache';
import { useTypedQuery } from '../helpers/useTypedQuery';
import { Moment } from 'moment';
import { useTypedMutation } from '../helpers/useTypedMutation';
import { usePressHeaderCacheUpdate } from './press.query';
import { useQueryClient } from '@tanstack/react-query';

export const useArticleFeed = () => {
  const { isLoading: isLoadingArticleFeed, data: articleFeed } = useTypedQuery(
    'get',
    '/communication/press/feed'
  );

  return { isLoadingArticleFeed, articleFeed };
};

export const useArticleHeader = () => {
  const { isLoading: isLoadingArticleHeader, data: articleHeader } =
    useTypedQuery('get', '/communication/press/header');

  return { isLoadingArticleHeader, articleHeader };
};

export type Option = {
  label: string;
  value: number;
};

export type TagOption = {
  value: string;
  label: string;
};

export type ArticleFilter = {
  fromDate?: Moment;
  toDate?: Moment;
  isRead?: boolean;
  // tag is expected to be a comma separated string of tag values
  // FIXME: why not pass a list and have the API align with that
  tags?: TagOption[];
  country?: Option;
  category?: Option;
  search?: string;
  isFavorite?: boolean;
  isPaper?: boolean;
  page?: number;
  isFeatured?: boolean;
  limit?: number;
};

export type ArticleFilterAPI = Omit<ArticleFilter, 'tags'> & {
  tags?: string;
};

// need to format api parameters:
// - is_favorite, is_featured, is_paper is not a real tags, these are filters in their own right
// - and tags is not a list, it's a string like '"tag-1", "tag-2", "..."'
export const formatArticleFilterAPI = (
  articleFilter: ArticleFilter
): ArticleFilterAPI => {
  const { tags, ...rest } = articleFilter;
  const filters: ArticleFilterAPI = {
    ...rest
  };

  if (articleFilter.tags) {
    let selectedTags = articleFilter.tags.map((tag) => tag.value);
    if (selectedTags.includes('is_favorite')) filters.isFavorite = true;
    if (selectedTags.includes('is_featured')) filters.isFeatured = true;
    if (selectedTags.includes('is_paper')) filters.isPaper = true;

    selectedTags = articleFilter.tags
      .filter(
        (tag) => !['is_favorite', 'is_featured', 'is_paper'].includes(tag.value)
      )
      .map((tag) => tag.label);

    if (selectedTags.length > 0) {
      filters.tags = selectedTags.map((tag: string) => `"${tag}"`).join(', ');
    }
  }

  return filters;
};

export const useArticles = (articleFilter: ArticleFilterAPI) => {
  const { isLoading: isLoadingArticles, data: articlesResponse } =
    useTypedQuery('get', '/communication/press/search', {
      params: {
        query: {
          page: articleFilter.page,
          from_date: articleFilter.fromDate?.format('YYYY-MM-DD'),
          to_date: articleFilter.toDate?.format('YYYY-MM-DD'),
          categories: articleFilter.category?.value,
          is_read: articleFilter.isRead,
          // FIXME: this is a value in the tags array, not a separate field
          is_favorite: articleFilter.isFavorite,
          // FIXME: this is a value in the tags array, not a separate field
          is_paper: articleFilter.isPaper,
          // FIXME: this is a value in the tags array, not a separate field
          is_featured: articleFilter.isFeatured,
          search: articleFilter.search,
          countries: articleFilter.country?.value,
          // FIXME: the API expects a string ["tag1", "tag2"]
          //        if no tags are selected, it should not be sent as a parameter
          //        specials tags (is_favorite, is_paper, is_featured) are not included
          tags: articleFilter?.tags,
          limit: articleFilter.limit
        }
      }
    });

  return { isLoadingArticles, articlesResponse };
};

export const useArticlesCacheUpdate = (articleFilter: ArticleFilterAPI) =>
  useTypedSetCache('get', '/communication/press/search', {
    params: {
      query: {
        page: articleFilter.page,
        from_date: articleFilter.fromDate?.format('YYYY-MM-DD'),
        to_date: articleFilter.toDate?.format('YYYY-MM-DD'),
        categories: articleFilter.category?.value,
        is_read: articleFilter.isRead,
        // FIXME: this is a value in the tags array, not a separate field
        is_favorite: articleFilter.isFavorite,
        // FIXME: this is a value in the tags array, not a separate field
        is_paper: articleFilter.isPaper,
        // FIXME: this is a value in the tags array, not a separate field
        is_featured: articleFilter.isFeatured,
        search: articleFilter.search,
        countries: articleFilter.country?.value,
        // FIXME: the API expects a string ["tag1", "tag2"]
        //        if no tags are selected, it should not be sent as a parameter
        //        specials tags (is_favorite, is_paper, is_featured) are not included
        tags: articleFilter?.tags,
        limit: articleFilter.limit
      }
    }
  });

export const useArticlesRemoveTagsMutation = (
  articleFilter: ArticleFilterAPI
) => {
  const updateHeaderCache = usePressHeaderCacheUpdate();
  const updateArticlesCache = useArticlesCacheUpdate(articleFilter);
  return useTypedMutation('post', `/communication/press/posts/remove_tags`, {
    mutationOpts: {
      onSuccess(data, _variables, _context) {
        // If no data, it means the tag was not created
        if (!data) {
          return;
        }
        updateHeaderCache((oldHeader: any) =>
          oldHeader
            ? {
                ...oldHeader,
                tags: data.tags
              }
            : oldHeader
        );
        updateArticlesCache((oldArticlesResponse: any) => {
          if (!oldArticlesResponse) return oldArticlesResponse;

          const newTagsId = data.tags.map((item) => item.id);

          return {
            ...oldArticlesResponse,
            results: oldArticlesResponse.results.map((article: any) => ({
              ...article,
              tags: article.tags.filter((item: any) =>
                newTagsId.includes(item.id)
              )
            }))
          };
        });
      }
    }
  });
};

export const useCreateTagMutation = () => {
  const updateHeaderCache = usePressHeaderCacheUpdate();
  const queryClient = useQueryClient();
  return useTypedMutation('post', `/communication/press/tags/create`, {
    mutationOpts: {
      onSuccess(data, _variables, _context) {
        // If no data, it means the tag was not created
        if (!data) {
          return;
        }
        // Add the new tag to the header cache
        updateHeaderCache((oldHeader: any) =>
          oldHeader
            ? {
                ...oldHeader,
                tags: [...oldHeader.tags, data]
              }
            : oldHeader
        );
        // Invalidate queries to ensure all components using tags get updated
        queryClient.invalidateQueries({
          queryKey: ['get', '/communication/press/header']
        });
      }
    }
  });
};
